const BASE = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "") + "/api/v1";
let token: string | null = localStorage.getItem("an_admin_token");

export function setToken(t: string | null) {
  token = t;
  if (t) localStorage.setItem("an_admin_token", t);
  else localStorage.removeItem("an_admin_token");
}
export function getToken() { return token; }

export type UserOut = {
  id: number;
  full_name: string;
  phone: string;
  role: string;
  workshop_id: number | null;
  branch_id: number | null;
};

// Backend xatoni {"error":{"code","message"}} konvertida qaytaradi. `message`
// oldingidek `Error.message` bo'lib qoladi (barcha eski chaqiruvlar ishlayveradi),
// `code` esa qo'shimcha — u bilan xatoni aynan tegishli maydon tagida
// ko'rsatish mumkin (masalan Ilova versiyasi sahifasi).
export class ApiError extends Error {
  code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = "ApiError";
    this.code = code;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const r = await fetch(`${BASE}${path}`, { ...init, headers });
  if (r.status === 401) {
    setToken(null);
    window.location.reload();
    throw new Error("unauthorized");
  }
  if (!r.ok) {
    let body: { error?: { message?: string; code?: string } } = {};
    try { body = await r.json(); } catch { /* */ }
    throw new ApiError(
      body.error?.message || r.statusText,
      body.error?.code || "http_error",
    );
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export type Tariff = "trial" | "standard" | "comfort" | "pro" | "max";
export type WorkshopStatus = "active" | "suspended" | "expired" | "archived";

export type Health = "green" | "yellow" | "red";

export type WorkshopRow = {
  id: number;
  name: string;
  owner_id: number | null;
  owner_name: string | null;
  owner_phone: string | null;
  tariff: Tariff;
  status: WorkshopStatus;
  trial_end: string | null;
  is_expired: boolean;
  clients_count: number;
  works_count: number;
  created_at: string;
  archived_at: string | null;
  // Salomatlik (retention) — backend hisoblaydi
  last_work_at: string | null;
  days_since_last_work: number | null;
  days_since_created: number;
  linked_client_count: number;
  link_rate: number | null;
  health: Health;
  reason: string;
  manual_messaging_enabled: boolean;
};

export type StaffActivity = {
  user_id: number;
  full_name: string;
  work_count: number;
  last_work_at: string | null;
};

export type WorkshopDetail = WorkshopRow & {
  branches: { id: number; name: string }[];
  employees_count: number;
  works_by_day: AnalyticsPoint[];
  staff: StaffActivity[];
  manual_msgs_24h: number;
  manual_msgs_7d: number;
  broadcast_msgs_24h: number;
  broadcast_msgs_7d: number;
};

export type Stats = {
  total_workshops: number;
  active_workshops: number;
  trial_workshops: number;
  expired_workshops: number;
  new_this_month: number;
  trial_ending_soon: number;
  unused_codes: number;
};

export type ActivationCode = {
  id: number;
  code: string;
  tariff: Tariff;
  used: boolean;
  workshop_id: number | null;
  workshop_name: string | null;
  created_at: string | null;
};

export type PlanInfo = { tariff: Tariff; price: number | null };

export type TicketType = "support" | "feedback";
export type TicketStatus = "new" | "seen" | "replied";

export type TicketSender = {
  id: number;
  full_name: string;
  phone: string;
  role: string;
  telegram_id: number | null;
};

export type TicketWorkshop = {
  id: number;
  name: string;
  tariff: Tariff;
  status: WorkshopStatus;
  is_expired: boolean;
};

export type SupportTicket = {
  id: number;
  type: TicketType;
  title: string;
  body: string;
  status: TicketStatus;
  admin_reply: string | null;
  replied_at: string | null;
  created_at: string;
  sender: TicketSender;
  workshop: TicketWorkshop;
};

export type ReplyResult = SupportTicket & { telegram_sent: boolean };

export type BotLeadBolim = "yakka" | "yagona_filial" | "kop_filial";
export type BotLeadStatus = "new" | "contacted" | "qualified" | "won" | "lost";

export type BotLead = {
  id: number;
  ism: string;
  telefon: string;
  telegram_id: number | null;
  bolim: BotLeadBolim | null;
  status: BotLeadStatus;
  assignee: string | null;
  note: string | null;
  next_follow_up_at: string | null;
  created_at: string;
  updated_at: string;
};

export type BotLeadCreate = Pick<BotLead, "ism" | "telefon" | "bolim" | "assignee" | "note" | "next_follow_up_at">;
export type BotLeadUpdate = Partial<Pick<BotLead, "status" | "assignee" | "note" | "next_follow_up_at">>;

export type AnalyticsPoint = { date: string; count: number };
export type TariffBucket = { tariff: Tariff; count: number };
export type TopWorkshop = { id: number; name: string; works_count: number };

export type Analytics = {
  days: number;
  new_workshops_by_day: AnalyticsPoint[];
  new_workshops_total: number;
  codes_activated_total: number;
  works_total: number;
  tariff_distribution: TariffBucket[];
  top_workshops: TopWorkshop[];
};

export type TariffRevenue = {
  tariff: Tariff;
  count: number;
  price: number | null;
  subtotal: number;
  share: number;
};

export type Revenue = {
  mrr: number;
  tariff_breakdown: TariffRevenue[];
  active_trials: number;
  trial_floor_estimate: number;
  standard_price: number;
};

export type FunnelStage = {
  key: string;
  label: string;
  count: number;
  pct_of_prev: number | null;
};

export type Funnel = {
  days: number;
  activation: FunnelStage[];
  qr: FunnelStage[];
};

export type DriverFunnel = {
  registered: number;
  has_car: number;
  active: number;
  has_car_pct: number | null;
  active_pct: number | null;
};

export type AppPlatform = "android" | "ios";

export type AppVersionRow = {
  platform: AppPlatform;
  latest_version: string;
  min_supported_version: string;
  store_url: string;
  release_notes_uz: string | null;
  updated_at: string;
};

export type AppVersionPatch = Partial<
  Pick<
    AppVersionRow,
    "latest_version" | "min_supported_version" | "store_url" | "release_notes_uz"
  >
>;

export const APP_PLATFORM_LABEL: Record<AppPlatform, string> = {
  android: "Android",
  ios: "iOS",
};

export const api = {
  // Logout must not trigger req()'s credential-clearing error side effects.
  async logout(): Promise<void> {
    const r = await fetch(`${BASE}/auth/logout`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (r.status !== 200 || (await r.json()).ok !== true) {
      throw new Error("logout_failed");
    }
  },
  login: (identifier: string, password: string) =>
    req<{ access_token: string; user: UserOut }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ identifier, password }),
    }),
  me: () => req<UserOut>("/auth/me"),

  workshops: (archived = false) =>
    req<WorkshopRow[]>(`/admin/workshops${archived ? "?archived=true" : ""}`),
  workshopDetail: (id: number) => req<WorkshopDetail>(`/admin/workshops/${id}`),
  createWorkshop: (data: Record<string, unknown>) =>
    req<{ workshop_id: number; owner_id: number; branch_id: number }>(
      "/admin/workshops",
      { method: "POST", body: JSON.stringify(data) }
    ),
  updateWorkshop: (id: number, data: Record<string, unknown>) =>
    req<WorkshopRow>(`/admin/workshops/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  setManualMessaging: (id: number, enabled: boolean) =>
    req<WorkshopRow>(`/admin/workshops/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ manual_messaging_enabled: enabled }),
    }),
  archiveWorkshop: (id: number) =>
    req<void>(`/admin/workshops/${id}`, { method: "DELETE" }),
  resetOwnerPassword: (id: number) =>
    req<{ ok: boolean; owner_id: number }>(
      `/admin/workshops/${id}/reset-password`,
      { method: "POST", body: JSON.stringify({}) }
    ),

  plans: () => req<PlanInfo[]>("/admin/plans"),

  stats: () => req<Stats>("/admin/stats"),
  analytics: (days: number) =>
    req<Analytics>(`/admin/analytics?days=${days}`),
  revenue: () => req<Revenue>("/admin/revenue"),
  funnel: (days: number) => req<Funnel>(`/admin/funnel?days=${days}`),
  driverFunnel: () => req<DriverFunnel>("/admin/drivers/funnel"),

  activationCodes: (opts?: { used?: boolean; q?: string }) => {
    const qs = new URLSearchParams();
    if (opts?.used !== undefined) qs.set("used", String(opts.used));
    if (opts?.q) qs.set("q", opts.q);
    const tail = qs.toString();
    return req<ActivationCode[]>(
      `/admin/activation-codes${tail ? "?" + tail : ""}`
    );
  },
  createActivationCode: (tariff: Tariff) =>
    req<ActivationCode>("/admin/activation-codes", {
      method: "POST",
      body: JSON.stringify({ tariff }),
    }),
  createActivationCodesBulk: (tariff: Tariff, count: number) =>
    req<ActivationCode[]>("/admin/activation-codes/bulk", {
      method: "POST",
      body: JSON.stringify({ tariff, count }),
    }),
  deleteActivationCode: (id: number) =>
    req<void>(`/admin/activation-codes/${id}`, { method: "DELETE" }),

  supportTickets: (opts?: { status?: TicketStatus; type?: TicketType; workshop_id?: number }) => {
    const qs = new URLSearchParams();
    if (opts?.status) qs.set("status", opts.status);
    if (opts?.type) qs.set("type", opts.type);
    if (opts?.workshop_id !== undefined) qs.set("workshop_id", String(opts.workshop_id));
    const tail = qs.toString();
    return req<SupportTicket[]>(`/admin/support/tickets${tail ? "?" + tail : ""}`);
  },
  botLeads: () => req<BotLead[]>("/admin/bot/leads"),
  createBotLead: (lead: BotLeadCreate) =>
    req<BotLead>("/admin/bot/leads", { method: "POST", body: JSON.stringify(lead) }),
  updateBotLead: (id: number, patch: BotLeadUpdate) =>
    req<BotLead>(`/admin/bot/leads/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  botHaydovchilar: () => req<BotLead[]>("/admin/bot/haydovchilar"),
  markLeadContacted: (id: number) =>
    req<BotLead>(`/admin/bot/leads/${id}/status`, { method: "PATCH" }),

  markTicketSeen: (id: number) =>
    req<SupportTicket>(`/admin/support/tickets/${id}/seen`, { method: "PATCH" }),
  replyTicket: (id: number, reply_text: string) =>
    req<ReplyResult>(`/admin/support/tickets/${id}/reply`, {
      method: "POST",
      body: JSON.stringify({ reply_text }),
    }),

  appVersions: () => req<AppVersionRow[]>("/admin/app-version"),
  // Qator hali bo'lmagan platformada (ios) ham shu chaqiruv ishlaydi — backend
  // uni yaratadi, migratsiya kerak emas.
  updateAppVersion: (platform: AppPlatform, patch: AppVersionPatch) =>
    req<AppVersionRow>(`/admin/app-version/${platform}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
};

export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  new: "Yangi",
  seen: "Ko'rildi",
  replied: "Javob berildi",
};

export const TICKET_TYPE_LABEL: Record<TicketType, string> = {
  support: "Aloqa",
  feedback: "Taklif/Shikoyat",
};


export const BOLIM_LABEL: Record<BotLeadBolim, string> = {
  yakka: "Usta (Yakka)",
  yagona_filial: "Yagona filial",
  kop_filial: "Ko'p filial",
};

export const HEALTH_LABEL: Record<Health, string> = {
  green: "Sog'lom",
  yellow: "Sust",
  red: "Xavf",
};

export const TARIFF_LABEL: Record<Tariff, string> = {
  trial: "Sinov",
  standard: "Standart",
  comfort: "Comfort",
  pro: "Pro",
  max: "Max",
};

// Tarif dropdownlari uchun yagona tartib (arzondan qimmatga). Barcha <select>lar
// shu ro'yxatdan map qiladi — yangi tarif qo'shilganda bitta joy o'zgaradi.
export const TARIFF_ORDER: Tariff[] = ["trial", "standard", "comfort", "pro", "max"];

const moneyFmt = new Intl.NumberFormat("uz-UZ", { maximumFractionDigits: 0 });

export function fmtMoney(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return moneyFmt.format(v) + " so'm";
}

export const STATUS_LABEL: Record<WorkshopStatus, string> = {
  active: "Faol",
  suspended: "To'xtatilgan",
  expired: "Muddati tugagan",
  archived: "Arxivlangan",
};

// Barcha vaqtlar O'zbekiston vaqtida (server UTC'da saqlaydi).
const TZ = "Asia/Tashkent";

// Naive (timezone'siz) ISO satrini UTC deb hisoblaymiz.
export function parseServerDate(s: string): Date {
  if (s.includes("T") && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s + "Z");
  return new Date(s);
}

export function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  return parseServerDate(s).toLocaleDateString("uz-UZ", { timeZone: TZ });
}

export function fmtDateTime(s: string | null | undefined): string {
  if (!s) return "—";
  const d = parseServerDate(s);
  return (
    d.toLocaleDateString("uz-UZ", { timeZone: TZ }) +
    " " +
    d.toLocaleTimeString("uz-UZ", { timeZone: TZ, hour: "2-digit", minute: "2-digit" })
  );
}

export function daysUntil(s: string | null | undefined): number | null {
  if (!s) return null;
  const ms = new Date(s).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}
